import type { ThemeConfig } from "antd";

// ─────────────────────────────────────────────────────────────────────────────
// COLOR PALETTE
// ─────────────────────────────────────────────────────────────────────────────
//
// Consumed by CSS modules as :root custom properties (--accent, --card-border,
// etc.) and directly in LoginPage.tsx inline styles.
//
// Do not introduce new color values without adding them here first.

export const palette = {
  // ── Brand accent ────────────────────────────────────────────────────────
  // Teal used for primary buttons, active states, selected menu items,
  // filter indicators, form submit buttons, and the topbar KPI accent line.
  accent: "#00bfa5",
  accentHover: "#00d4b7",
  accentLight: "rgba(0, 191, 165, 0.12)", // low-opacity tint for hover bg

  // ── Application backgrounds ──────────────────────────────────────────────
  pageBg: "#f4f7fa", // content area behind all cards
  surface: "#ffffff", // card, form section, table card backgrounds
  tableHeaderBg: "#f8fafc", // thead background override
  tableRowHover: "#f7fbff", // tbody row hover

  // ── Sidebar (dark navy shell) ────────────────────────────────────────────
  sidebarBg: "#0d1b2a", // Layout.Sider background
  sidebarBgDeep: "#091322", // Layout trigger / collapse area
  sidebarBorder: "rgba(255,255,255,0.06)",
  sidebarText: "#7a9ab5", // default nav item text
  sidebarTextBright: "#c8dded", // hover nav item text
  sidebarTextMuted: "#4a6478", // nav section labels, collapse btn

  // ── Typography ───────────────────────────────────────────────────────────
  textPrimary: "#1a2d3f", // headings, table cell text, input text
  textSecondary: "#637889", // subtitles, labels, meta text, placeholder
  textMuted: "#aab8c8", // ticket numbers, empty states, secondary meta

  // ── Borders / dividers ───────────────────────────────────────────────────
  cardBorder: "#e8edf4", // card borders, toolbar dividers, table row dividers
  rowDivider: "#f0f4f8", // tbody row borders (lighter than cardBorder)

  // ── Topbar ───────────────────────────────────────────────────────────────
  topbarBg: "#ffffff",
  topbarBorder: "#e8edf4",

  // ── Status badges ────────────────────────────────────────────────────────
  // Three-value groups: [text color, background fill, border color]
  statusNew: { text: "#0958d9", bg: "#e6f4ff", border: "#91caff" },
  statusProgress: { text: "#d46b08", bg: "#fff7e6", border: "#ffd591" },
  statusDone: { text: "#389e0d", bg: "#f6ffed", border: "#b7eb8f" },

  // ── Priority ─────────────────────────────────────────────────────────────
  priorityHigh: "#dc2626", // red — also used for required field asterisks
  priorityMedium: "#d97706", // amber
  priorityLow: "#637889",

  // ── Service types ─────────────────────────────────────────────────────────
  // Bg is the semi-transparent fill; text is the readable foreground.
  serviceS1: { text: "#7c3aed", bg: "rgba(124,58,237,0.09)" }, // Inspection — violet
  serviceS2: { text: "#dc2626", bg: "rgba(220,38,38,0.08)" }, // Repair — red
  serviceS3: { text: "#0891b2", bg: "rgba(8,145,178,0.09)" }, // Cleaning — cyan

  // ── Dashboard semantic colors ─────────────────────────────────────────────
  // Used for KPI accent bars, breakdown bar fills, and dashboard badges.
  dashBlue: "#2563eb",
  dashOrange: "#f59e0b",
  dashGreen: "#10b981",
  dashRed: "#ef4444",
  dashPurple: "#7c3aed",
  dashCyan: "#06b6d4",

  // ── Supply categories ─────────────────────────────────────────────────────
  // Line color marking supplies of the same category in the Work Order
  // Supplies tab; keys match the supply category values from the API.
  supplyCategory: {
    RESINS_AND_HARDENERS: "#f59e0b",
    PUTTY: "#b45309",
    PAINTS: "#ef4444",
    SEALANTS_AND_PRIMERS: "#2563eb",
    TOWER_COATINGS: "#7c3aed",
    SPECIAL_WORKS: "#db2777",
    OTHER_MATERIALS: "#637889",
    FABRIC: "#10b981",
    VORTEX_GENERATORS: "#06b6d4",
  },
  supplyCategoryNone: "#aab8c8",
} as const;

// ─────────────────────────────────────────────────────────────────────────────
// GLOBAL THEME  (root <ConfigProvider> in main.tsx)
// ─────────────────────────────────────────────────────────────────────────────

export const globalTheme: ThemeConfig = {
  // cssVar injects every design token as a CSS custom property on :root.
  // Variables follow the pattern --ant-{token-name-kebab}.
  // This allows CSS modules to consume tokens without JS coupling:
  //   padding: var(--ant-padding-md);
  cssVar: { prefix: "ant" },

  token: {
    fontFamily: "'DM Sans',sans-serif",
    fontFamilyCode: "'JetBrains Mono',monospace",
    colorPrimary: "#00bfa5",
    colorTextBase: "#000000",
    colorLink: "#1677ff",
    colorError: "#ff4d4f",
    colorWarning: "#faad14",
    colorSuccess: "#52c41a",
    colorInfo: "#7ca3d9",
    colorBgBase: "#ffffff",
  },

  components: {
    // Table
    // ─────
    // Setting cell padding here via the component token system means CSS
    // modules no longer need `padding: X !important` on thead/tbody cells.
    // Both header and body cells share the same cellPaddingBlock value.
    Table: {
      cellPaddingBlock: 12, // paddingSM  — vertical cell padding
      cellPaddingInline: 16, // padding    — horizontal cell padding
    },

    // Form
    // ────
    // Standardises the vertical gap between form fields to one spacing unit
    // (matches the `padding` token). Without this, AntD's default is 24px
    // which feels loose in the dense form layouts used here.
    Form: {
      itemMarginBottom: 16, // padding
    },
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// SIDEBAR THEME  (scoped <ConfigProvider> in SideMenu.tsx)
// ─────────────────────────────────────────────────────────────────────────────
//
// Wrapped around Layout.Sider only. Overrides do not bleed into the rest
// of the app because ConfigProvider inherits downward, not upward.
//
// The Menu component uses theme="dark", which has its own separate token
// namespace (darkItem*). These cannot be set via globalTheme because the
// dark variant tokens are resolved separately from the light ones.

// ─────────────────────────────────────────────────────────────────────────────
// LOGIN PAGE THEME  (scoped <ConfigProvider> in LoginPage.tsx)
// ─────────────────────────────────────────────────────────────────────────────
//
// Scoped to the login page only. Overrides component tokens so the CSS module
// no longer needs !important to beat antd's generated styles.

export const loginTheme: ThemeConfig = {
  components: {
    Card: {
      borderRadiusLG: 12,
      colorBorderSecondary: palette.cardBorder,
    },
    Input: {
      borderRadius: 6,
      fontSizeLG: 14,
    },
    Button: {
      borderRadiusLG: 6,
      fontSizeLG: 14,
      controlHeightLG: 44,
    },
  },
};

export const sidebarTheme: ThemeConfig = {
  components: {
    Menu: {
      darkItemBg: "transparent", // no background on idle items
      darkSubMenuItemBg: "rgba(0, 0, 0, 0.18)", // subtle tint behind sub-items
      darkItemSelectedBg: "rgba(0, 191, 165, 0.14)", // accent tint on active item
      darkItemSelectedColor: "#00BFA5", // accent text on active item
      darkItemHoverBg: "rgba(255, 255, 255, 0.05)", // hover tint
      darkItemColor: "#7A9AB5", // palette.sidebarText
      darkItemHoverColor: "#C8DDED", // palette.sidebarTextBright
      itemHeight: 38, // compact row height (AntD default is 40px)
      fontSize: 13, // matches app body size
    },

    Layout: {
      siderBg: "#0D1B2A", // palette.sidebarBg
      triggerBg: "#0A1520", // palette.sidebarBgDeep — collapse trigger area
    },
  },
};
